import useAlbumDetails from "@hooks/useAlbumDetails";
import useAlbumTracks from "@hooks/useAlbumTracks";
import useCurrentlyPlayingSong from "@hooks/useCurrentlyPlayingSong";
import useSpotifyTracks, { QueuedTrack } from "@hooks/useSpotifyTracks";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FavoriteIcon from "@mui/icons-material/Favorite";
import NonFavoriteIcon from "@mui/icons-material/FavoriteBorder";
import { Button, Collapse } from "@mui/material";
import { styled, useTheme } from "@mui/system";
import NoTrackPlaying from "@pages/LyricsView/NoTrackPlaying";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatMilliseconds } from "src/utils";

const AlbumPageContainer = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center"
});
const AlbumTable = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  marginBottom: "80px",
  tableLayout: "fixed",
  "& .optional-column": {
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  }
}));

const TableHeader = styled("th")(({ theme }) => ({
  padding: "4px", // Reduced padding
  textAlign: "left",
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.secondary
}));

const TableCell = styled("td")(({ theme }) => ({
  padding: "4px", // Reduced padding
  textAlign: "left",
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const TableRow = styled("tr")<{ isPlaying: boolean }>(({ theme, isPlaying }) => ({
  cursor: "pointer",
  height: "64px",
  backgroundColor: isPlaying ? theme.palette.action.selected : "inherit", // Highlight current playing song
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));

const AlbumCover = styled("img")({
  width: "clamp(112px, 22vw, 150px)",
  height: "clamp(112px, 22vw, 150px)",
  marginBottom: "20px"
});

const AlbumHeaderButton = styled("button")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  border: "none",
  background: "none",
  color: theme.palette.text.primary,
  cursor: "pointer",
  padding: 0,
  margin: 0,
  maxWidth: "100%",
  "& h1": {
    fontSize: "clamp(1.55rem, 4vw, 2rem)",
    lineHeight: 1.15,
    margin: "0 0 20px",
    overflowWrap: "anywhere"
  },
  "&:hover h1": {
    textDecoration: "underline"
  }
}));

// New styles for specific columns
const TitleCell = styled(TableCell)({
  overflowWrap: "anywhere",
  width: "auto"
});

const SmallCell = styled(TableCell)({
  width: "5%", // Make popularity and favorite columns take minimal space
  minWidth: "40px",
  textAlign: "center",
  padding: "2px" // Even smaller padding
});

const QueueDebugSection = styled("div")(({ theme }) => ({
  boxSizing: "border-box",
  width: "100%",
  marginBottom: "16px",
  padding: "12px",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
  borderRadius: "8px",
  fontSize: "0.85rem"
}));

const QueueDebugHeader = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  padding: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  color: theme.palette.text.secondary,
  fontSize: "0.9rem",
  textAlign: "left"
}));

const QueueItem = styled("div")(({ theme }) => ({
  padding: "4px 0",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": { borderBottom: "none" }
}));

const AlbumPage: React.FunctionComponent = () => {
  const theme = useTheme();
  const { albumId: routeAlbumId } = useParams<{ albumId: string }>();
  const currentlyPlayingSong = useCurrentlyPlayingSong();
  const currentAlbum = currentlyPlayingSong?.currentlyPlayingObject?.item?.album;
  const {
    album: routeAlbum,
    loading: routeAlbumLoading,
    error: routeAlbumError
  } = useAlbumDetails(routeAlbumId);
  const album = routeAlbum ?? currentAlbum;
  const albumId = album?.id;
  const { tracks, loading, error } = useAlbumTracks(albumId);
  const {
    stickToAlbum,
    resumeOriginalPlaylist,
    isStickingToAlbum,
    switchTrack,
    pendingTracks,
    lastQueuedTrack,
    queueError
  } = useSpotifyTracks();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [queueDebugOpen, setQueueDebugOpen] = useState(false);
  const albumTitle = album?.name || "Album Title Placeholder";
  const albumCoverUrl = album?.images?.[0]?.url || "https://via.placeholder.com/150";
  const primaryArtist =
    routeAlbum?.artists?.[0] ?? currentlyPlayingSong?.currentlyPlayingObject?.item?.artists?.[0];
  const currentTrackRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (currentTrackRef.current) {
      currentTrackRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }, [currentlyPlayingSong?.currentlyPlayingObject?.item?.id]);

  if (routeAlbumLoading || (routeAlbumId && !routeAlbum && !routeAlbumError) || loading) {
    return <div>Loading...</div>;
  }
  if (routeAlbumError) return <div>{routeAlbumError}</div>;
  if (error) return <div>{error}</div>;

  function playSong(trackID: string): void {
    switchTrack(trackID);
    navigate("/");
  }

  function navigateToArtist(): void {
    if (!primaryArtist || !albumId) return;
    navigate(`/artist/${primaryArtist.id}`, { state: { fromAlbumId: albumId } });
  }

  const handleTogglePlayback = async () => {
    if (!albumId) return;

    setIsLoading(true);
    try {
      if (isStickingToAlbum) {
        await resumeOriginalPlaylist();
      } else {
        await stickToAlbum(albumId);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return albumId === undefined ? (
    <NoTrackPlaying></NoTrackPlaying>
  ) : (
    <AlbumPageContainer>
      <Button
        variant="contained"
        color={isStickingToAlbum ? "secondary" : "primary"}
        onClick={handleTogglePlayback}
        disabled={isLoading}
        sx={{ mb: 2 }}
      >
        {isLoading ? "Processing..." : isStickingToAlbum ? "Resume Playlist" : "Stick to Album"}
      </Button>

      {/* Queue debug section - visible when sticking to album */}
      {isStickingToAlbum && (
        <QueueDebugSection theme={theme}>
          <QueueDebugHeader
            theme={theme}
            onClick={() => setQueueDebugOpen(prev => !prev)}
            type="button"
          >
            {queueDebugOpen ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
            <span>
              Queue: {pendingTracks.length} pending
              {lastQueuedTrack && ` • In Spotify queue: ${lastQueuedTrack.name}`}
              {queueError && ` • Error: ${queueError}`}
            </span>
          </QueueDebugHeader>
          <Collapse in={queueDebugOpen}>
            <div style={{ marginTop: 8 }}>
              {lastQueuedTrack && (
                <QueueItem theme={theme}>
                  <strong>Now queued in Spotify:</strong> {lastQueuedTrack.name}
                </QueueItem>
              )}
              {pendingTracks.length > 0 && (
                <>
                  <div style={{ marginTop: 8, marginBottom: 4 }}>
                    <strong>Pending (to be queued):</strong>
                  </div>
                  {pendingTracks.map((track: QueuedTrack, idx: number) => (
                    <QueueItem key={`${track.uri}-${idx}`} theme={theme}>
                      {idx + 1}. {track.name}
                    </QueueItem>
                  ))}
                </>
              )}
              {!lastQueuedTrack && pendingTracks.length === 0 && (
                <div style={{ marginTop: 8 }}>No tracks in queue.</div>
              )}
            </div>
          </Collapse>
        </QueueDebugSection>
      )}

      <AlbumHeaderButton theme={theme} onClick={navigateToArtist} type="button">
        <AlbumCover src={albumCoverUrl} alt="Album Cover" />
        <h1>{albumTitle}</h1>
      </AlbumHeaderButton>
      <AlbumTable theme={theme}>
        <thead>
          <tr>
            <TableHeader theme={theme} style={{ width: "5%" }}>
              #
            </TableHeader>
            <TableHeader theme={theme} style={{ flexGrow: 1 }}>
              Title
            </TableHeader>
            <TableHeader className="optional-column" theme={theme} style={{ width: "12%" }}>
              Popularity
            </TableHeader>
            <TableHeader className="optional-column" theme={theme} style={{ width: "12%" }}>
              Duration
            </TableHeader>
            <TableHeader theme={theme} style={{ width: "5%" }}></TableHeader>
          </tr>
        </thead>
        <tbody>
          {tracks.map((track, index) => (
            <TableRow
              key={track.id}
              theme={theme}
              onClick={() => playSong(track.id)}
              isPlaying={currentlyPlayingSong?.currentlyPlayingObject?.item?.id === track.id}
              ref={
                currentlyPlayingSong?.currentlyPlayingObject?.item?.id === track.id
                  ? currentTrackRef
                  : null
              }
            >
              <SmallCell theme={theme}>{index + 1}</SmallCell>
              <TitleCell theme={theme}>{track.name}</TitleCell>
              <SmallCell className="optional-column" theme={theme}>
                {track.popularity}
              </SmallCell>
              <SmallCell className="optional-column" theme={theme}>
                {formatMilliseconds(track.duration_ms)} min
              </SmallCell>
              <SmallCell theme={theme}>
                {track.favorite ? <FavoriteIcon /> : <NonFavoriteIcon />}
              </SmallCell>
            </TableRow>
          ))}
        </tbody>
      </AlbumTable>
    </AlbumPageContainer>
  );
};

export default AlbumPage;
